/**
 * lib/netWorthJourney.js — v3.7.6 Phase 84.30
 *
 * Turns a snapshot { netWorth, delta30, delta180 } into a short,
 * human-friendly "journey" line for the Net Worth KPI. The exact
 * phrasing depends on:
 *   • sign of the current net worth (positive vs negative territory)
 *   • sign + magnitude of the recent cash-flow delta
 *   • how close to "steady" the delta is (below 1% of |netWorth| →
 *     phrased as "roughly steady" so a stray 200-rupee txn doesn't
 *     read as a movement)
 *   • whether the user is trending toward zero from below (delta > 0
 *     while netWorth is still negative → we celebrate the momentum
 *     with either "Improved by X…" or, when the improvement is
 *     small but consistent, "Moving toward positive territory")
 *
 * Returns { subValue, subValueClass } that plug straight into the
 * existing KPI component. Returns {} when we don't have enough signal
 * (no txns in the last 30 days) so the KPI renders unchanged.
 */
import React from 'react';
import { ArrowUp, ArrowDown, Minus, Sparkles } from 'lucide-react';
import { formatMoney } from './format';

// Threshold below which we consider the change "steady". Percent of
// the absolute net worth. Keeps micro-moves from producing a noisy line.
const STEADY_PCT = 0.01;
// Minimum absolute value in the base currency below which we treat any
// delta as noise (works for user with |netWorth|≈0 where percent would
// misbehave). ~= AED / USD 100 or ₹5,000.
const MIN_MEANINGFUL_BASE = 100;

/**
 * @param {object} args
 * @param {number} args.netWorth   current net worth in base currency
 * @param {number} args.delta30    net cash flow over last 30 days
 * @param {number} args.delta180   net cash flow over last 180 days
 * @param {string} args.currency   base currency code
 * @returns {{ subValue?: React.ReactNode, subValueClass?: string }}
 */
export function buildNetWorthJourney({ netWorth, delta30, delta180, currency }) {
  const nw     = Number(netWorth  || 0);
  const d30    = Number(delta30   || 0);
  const d180   = Number(delta180  || 0);
  const absNw  = Math.abs(nw);
  const abs30  = Math.abs(d30);
  const abs180 = Math.abs(d180);

  // No meaningful signal → say nothing (keep the KPI clean).
  if (abs30 < MIN_MEANINGFUL_BASE && abs180 < MIN_MEANINGFUL_BASE) {
    return {};
  }

  // "Steady" branch — the 30-day change is small vs the total. Use the
  // 180-day figure if it tells a stronger story; otherwise just say
  // steady. Applies to both positive and negative net worth users.
  const steady30 = abs30 < Math.max(MIN_MEANINGFUL_BASE, absNw * STEADY_PCT);
  if (steady30 && abs180 < Math.max(MIN_MEANINGFUL_BASE, absNw * STEADY_PCT * 6)) {
    return {
      subValue: (
        <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-steady">
          <Minus className="h-3 w-3" />
          Roughly steady over the last month
        </span>
      ),
      subValueClass: 'text-muted-foreground',
    };
  }

  // Choose the primary window: prefer 30-day when it's clearly moving,
  // fall back to the 180-day story otherwise.
  const use180 = steady30 && abs180 >= MIN_MEANINGFUL_BASE;
  const delta  = use180 ? d180 : d30;
  const windowLabel = use180 ? 'in the last 6 months' : 'in the last 30 days';
  const abs    = Math.abs(delta);
  const moneyStr = formatMoney(abs, currency, { compact: true });

  // Journey framings.
  //
  // (a) Deep-negative user climbing toward zero — celebrate the direction,
  //     mention the "moving toward positive territory" flavour when they're
  //     less than 6 months of improvement away from crossing zero.
  if (nw < 0 && delta > 0) {
    const monthsToCross = use180 ? (absNw / (abs / 6)) : (absNw / abs);
    if (monthsToCross > 0 && monthsToCross < 6) {
      return {
        subValue: (
          <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-crossing">
            <Sparkles className="h-3 w-3" />
            Moving toward positive territory
          </span>
        ),
        subValueClass: 'text-emerald-700 dark:text-emerald-400',
      };
    }
    return {
      subValue: (
        <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-improving">
          <ArrowUp className="h-3 w-3" />
          Improved by {moneyStr} {windowLabel}
        </span>
      ),
      subValueClass: 'text-emerald-700 dark:text-emerald-400',
    };
  }

  // (b) Negative user drifting deeper — clear signal without alarm.
  if (nw < 0 && delta < 0) {
    return {
      subValue: (
        <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-worsening">
          <ArrowDown className="h-3 w-3" />
          Down {moneyStr} {windowLabel}
        </span>
      ),
      subValueClass: 'text-[hsl(var(--destructive))]',
    };
  }

  // (c) Positive user growing — the classic "+X since last month".
  if (nw >= 0 && delta > 0) {
    return {
      subValue: (
        <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-growing">
          <ArrowUp className="h-3 w-3" />
          +{moneyStr} {windowLabel}
        </span>
      ),
      subValueClass: 'text-emerald-700 dark:text-emerald-400',
    };
  }

  // (d) Positive user losing ground — factual, not scary.
  return {
    subValue: (
      <span className="inline-flex items-center gap-1" data-testid="net-worth-journey-slipping">
        <ArrowDown className="h-3 w-3" />
        −{moneyStr} {windowLabel}
      </span>
    ),
    subValueClass: 'text-[hsl(var(--destructive))]',
  };
}
